import { getOssUrl } from '@/utils'
import {
  resolveStaticAssetRuntimeConfig,
  resolveTendaPublicStaticAssetRuntimeConfig,
} from '@/utils/staticAssetConfig'
import {
  shouldUseStaticAssetOrigin,
  shouldUseTendaPublicStaticAssetOrigin,
} from '@/utils/staticAssetManifest'

export default function useBasePath() {
  const config = useRuntimeConfig()
  let baseURL = config.public.appBase || '/'
  const legacyStaticAssetConfig = resolveStaticAssetRuntimeConfig(config.public)
  const tendaPublicStaticAssetConfig = resolveTendaPublicStaticAssetRuntimeConfig(config.public)

  baseURL = baseURL.endsWith('/') ? baseURL : `${baseURL}/`

  /**
   * @description 仅对已上传的大图切到 OSS，避免未迁移的小图直接炸成 404。
   * @param {string} normalizedPath - 标准化后的 public 路径。
   * @returns {string}
   */
  const resolveManagedStaticAsset = (normalizedPath, options = {}) => {
    if (!shouldUseStaticAssetOrigin(normalizedPath)) {
      return ''
    }

    const { staticAssetOrigin, staticAssetPrefix } = shouldUseTendaPublicStaticAssetOrigin(
      normalizedPath
    )
      ? tendaPublicStaticAssetConfig
      : legacyStaticAssetConfig

    if (!staticAssetOrigin) return ''

    const ossAssetUrl = `${staticAssetOrigin}${staticAssetPrefix}${normalizedPath}`

    if (options.disableOssFormat) {
      return ossAssetUrl
    }

    return getOssUrl(ossAssetUrl)
  }

  /**
   * @description 自动为本地路径拼接 Base URL，并让已迁移的大图复用 OSS 处理链。
   * @param {string} path - 原始路径。
   * @returns {string}
   */
  const withBase = (path, options = {}) => {
    if (!path) return ''

    if (/^(https?:|data:|blob:|\/\/|mailto:|tel:|javascript:)/i.test(path)) {
      return path
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    const managedStaticAssetUrl = resolveManagedStaticAsset(normalizedPath, options)

    if (managedStaticAssetUrl) {
      return managedStaticAssetUrl
    }

    if (normalizedPath.startsWith(baseURL)) {
      return normalizedPath
    }

    return `${baseURL}${normalizedPath.slice(1)}`
  }

  /**
   * @description 封装打开外部页面的方法，保留 Base URL 处理与 noopener 安全兜底。
   * @param {string} url - 目标路径。
   * @param {string} [target='_blank'] - 打开方式。
   */
  const openExternal = (url, target = '_blank') => {
    if (!import.meta.client || !url) return

    const resolvedUrl = withBase(url)
    const isNewWindow = target && !['_self', '_parent', '_top'].includes(target)
    const features = isNewWindow ? 'noopener,noreferrer' : ''
    const win = window.open(resolvedUrl, target, features)

    if (win && isNewWindow) {
      try {
        win.opener = null
      } catch {
        // noop
      }
    }
  }

  return { withBase, openExternal }
}
